import { Injectable, inject } from '@angular/core';
import { ReportingApiService } from '@api-new/reportingservice';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { loadInitialPlatformData } from '@shared/store/common.actions';
import { INBOX_FILTER, INBOX_FILTER_ALL } from '@shared/store/inbox/constants/inbox-filter';
import {
  getInbox,
  getInboxFailure,
  getInboxSuccess,
  markAllInboxMessagesAsRead,
  markAllInboxMessagesAsReadFailure,
  markAllInboxMessagesAsReadSuccess,
  markInboxMessageAsRead,
  markInboxMessageAsReadFailure,
  markInboxMessageAsReadSuccess,
} from '@shared/store/inbox/inbox.actions';
import { selectFilteredInboxMessages, selectInboxFilter } from '@shared/store/inbox/inbox.selectors';
import { of } from 'rxjs/internal/observable/of';
import { catchError, map, switchMap, withLatestFrom } from 'rxjs/operators';

@Injectable()
export class InboxEffects {
  private readonly reportingApiService = inject(ReportingApiService);
  private readonly store = inject(Store);
  private readonly actions$ = inject(Actions);

  handleLoadInitialPlatformData = createEffect(() => this.actions$.pipe(ofType(loadInitialPlatformData), map(getInbox)));

  getInbox = createEffect(() =>
    this.actions$.pipe(
      ofType(getInbox),
      switchMap(() => this.reportingApiService.HTTP_CP_GetInbox()),
      map((response) => getInboxSuccess({ response })),
      catchError((error) => of(getInboxFailure({ error }))),
    ),
  );

  markInboxMessageAsRead = createEffect(() =>
    this.actions$.pipe(
      ofType(markInboxMessageAsRead),
      switchMap(({ message }) =>
        this.reportingApiService.HTTP_CP_MarkInboxMessageAsRead({ activityLogId: message.activityLog.id }).pipe(
          map(() => markInboxMessageAsReadSuccess({ message })),
          catchError((error) => of(markInboxMessageAsReadFailure({ error }))),
        ),
      ),
    ),
  );

  markAllInboxMessagesAsRead = createEffect(() =>
    this.actions$.pipe(
      ofType(markAllInboxMessagesAsRead),
      withLatestFrom(this.store.select(selectFilteredInboxMessages), this.store.select(selectInboxFilter)),
      switchMap(([_, messages, filter]) =>
        this.reportingApiService
          .HTTP_CP_MarkInboxMessageAsRead({
            bulk: {
              lastReadMessageAt: messages[0].activityLog.createdAt,
              activityLogEntityTypes: filter != null ? INBOX_FILTER[filter] : INBOX_FILTER_ALL,
            },
          })
          .pipe(
            map(() => markAllInboxMessagesAsReadSuccess()),
            catchError((error) => of(markAllInboxMessagesAsReadFailure({ error }))),
          ),
      ),
    ),
  );
}
